'use client';

import React, {useMemo, useEffect} from 'react';
import {useCommonData} from '@udemy-v2/common-data';
import {PageForType} from './types/page';

import './global.scss';
import styles from './page.module.scss';

import {HeroSection} from './components/hero-section/hero-section';
import {SolutionsSection} from './components/solutions-section/solutions-section';
import {Stats} from './components/stats-section/stats';
import {Features} from './components/features/features';
import {Testimonials} from './components/testimonials-section/testimonials';
import {IndeedCta} from './components/indeed-cta/indeed-cta';

import {
    SubscriptionPlanProductType,
    SubscriptionStatus,
    useSubscriptionPlansQuery,
} from '@/generated/graphql';
import {SubscriptionPlansQueryVariables} from '@/generated/graphql';
import {useViewport, withinOrWiderThan} from '@udemy-v2/react-layout-components';
import {HeroSectionMobile} from './components/hero-section/hero-section-mobile';
import {IndeedCtaMobile} from './components/indeed-cta/indeed-cta-mobile';
import {ComponentLoader} from './components/component-loader/component-loader';
import {PageProvider} from './context/page-context';
import assetLoader from '@/utils/asset-loader';
import ReactDOM from 'react-dom';

function LandingPage() {
    useEffect(() => {
        ReactDOM.preload(assetLoader({src: '/fonts/UdemySansWeb-Light.woff2'}), {
            as: 'font',
            crossOrigin: 'anonymous',
        });
        ReactDOM.preload(assetLoader({src: '/fonts/UdemySansWeb-Medium.woff2'}), {
            as: 'font',
            crossOrigin: 'anonymous',
        });
    }, []);

    const {range} = useViewport();
    const isWideViewport = range && withinOrWiderThan(range, 'xl');

    const Hero = isWideViewport ? HeroSection : HeroSectionMobile;
    const Indeed = isWideViewport ? IndeedCta : IndeedCtaMobile;

    const {visitor} = useCommonData();

    const variables: SubscriptionPlansQueryVariables = {
        filters: {
            productType: SubscriptionPlanProductType.Consumersubscription,
        },
    };

    const {data} = useSubscriptionPlansQuery(variables);

    const pageFor = useMemo<PageForType | null>(() => {
        if (!data?.subscriptionPlans) return null;
        if (
            visitor?.countryCode === 'KR' ||
            visitor?.countryCode === 'CN' ||
            visitor?.countryCode === 'VN' ||
            visitor?.countryCode === 'JP' ||
            visitor?.countryCode === 'IN'
        ) {
            return 'mx';
        }
        if (
            data?.currentSubscriptionEnrollment?.subscribedPlan?.productType ===
                SubscriptionPlanProductType.Consumersubscription &&
            [SubscriptionStatus.Active, SubscriptionStatus.Trial].includes(
                data?.currentSubscriptionEnrollment?.status,
            )
        ) {
            return 'mx';
        }
        return data.subscriptionPlans.length > 0 ? 'pp' : 'mx';
    }, [data, visitor]);

    useEffect(() => {
        fetch(`/${process.env.APP_NAME}/api/target${location.search}`, {
            headers: {
                referrer: document.referrer,
            },
        })
            .then((response) => response.json())
            .then(() => console.log('Targeting success'))
            .catch(() => console.error('Error fetching targeting data'));
    }, []);

    return (
        <PageProvider pageFor={pageFor}>
            <main className={styles['indeed__main-container']}>
                <div className={styles['indeed__content-wrapper']}>
                    <div className={styles['indeed__hero-container']}>
                        {range && (
                            <Hero
                                pageFor={pageFor}
                                subscriptionPlans={data?.subscriptionPlans}
                                countryCode={visitor?.countryCode}
                            />
                        )}
                    </div>
                    <SolutionsSection imageType={isWideViewport ? 'desktop' : 'mobile'} />
                    <ComponentLoader pageFor={pageFor} Component={Stats} />
                    <Testimonials />
                    <ComponentLoader
                        pageFor={pageFor}
                        subscriptionPlans={data?.subscriptionPlans}
                        countryCode={visitor?.countryCode}
                        Component={Features}
                    />
                    <Indeed />
                </div>
            </main>
        </PageProvider>
    );
}

export {LandingPage};
